import { devtools } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import {
  ApiFunctionType,
  TABLE_INITIAL_STATE,
  TableState
} from '@store/table-store/table.model.ts';
import { withCallState } from '@shared/store-features/call-state-feature.ts';
import { CallStateEnum } from '@models/call-state.model.ts';
import { SnackbarType } from '@models/app-snackbar.model.ts';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import { ApiResponse } from '@models/api.model.ts';
import axios from 'axios';
import { SortOrder } from '@models/table.model.ts';

const createTableStore = () =>
  createStore<TableState>()(
    devtools(
      (setState, getState) => ({
        ...TABLE_INITIAL_STATE,
        ...withCallState(getState),

        setApiFunctionAndFetchData: async (
          apiFunc: ApiFunctionType,
          customParams = {}
        ) => {
          const { showSnackbar } = useSnackbarStore.getState();
          setState({ apiFunction: apiFunc });

          const { search, filters, between, sort, pagination } = getState();
          const [column, order] = sort;

          setTimeout(() => {
            setState({ callState: CallStateEnum.Loading });
          });

          const filterData: Record<string, string | number> = {};
          const filterInData: Record<string, (string | number)[]> = {};
          for (const [key, value] of Object.entries(filters)) {
            if (value.length > 1) {
              filterInData[key] = value;
            } else if (value.length === 1) {
              filterData[key] = value[0];
            }
          }

          const searchData: Record<string, string> = {};
          for (const [key, value] of Object.entries(search)) {
            if (value !== '') {
              searchData[key] = value;
            }
          }

          // const betweenData: Record<string, [string, string]> = {};
          // for (const [key, value] of Object.entries(between ?? {})) {
          //   const { from, to } = value;
          //   if (from && to) {
          //     betweenData[key] = [from, to];
          //   }
          // }

          const query: Record<string, any> = {
            search: searchData,
            between,
            filter: filterData,
            filter_in: filterInData,
            page: pagination.page,
            limit: pagination.rowsPerPage,
            ...customParams
          };

          if (column && order) {
            query.sort = `${column},${order}`;
          }

          const params = { query };

          try {
            const response: ApiResponse<any> = await apiFunc(params);
            const { rows, total, page, limit } = response;

            const totalPages = Math.ceil(total / limit);
            let currentPage = page;

            if (rows.length === 0 && currentPage > 1 && total > 0) {
              const prevPage = currentPage - 1;

              setState((s) => ({
                pagination: { ...s.pagination, page: prevPage }
              }));

              return getState().setApiFunctionAndFetchData(apiFunc, {
                ...customParams,
                page: prevPage
              });
            }

            if (currentPage > totalPages && totalPages > 0) {
              currentPage = totalPages;
            }

            setState({
              tableData: rows,
              pagination: {
                ...getState().pagination,
                page: currentPage,
                rowsPerPage: limit,
                totalPages
              },
              callState: CallStateEnum.Loaded
            });
          } catch (error: unknown) {
            setState({ callState: CallStateEnum.Error });

            if (axios.isAxiosError(error)) {
              const message = error.response?.data?.message;
              showSnackbar(message, SnackbarType.Error);
            }
          }
        },

        updateSearch: (columnId, value) => {
          setState((state) => ({
            search: { ...state.search, [columnId]: value },
            pagination: { ...state.pagination, page: 1 }
          }));
          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        updateFilters: (columnId, selectedFilters) => {
          setState((state) => ({
            filters: {
              ...state.filters,
              [columnId]: selectedFilters
            },
            pagination: { ...state.pagination, page: 1 }
          }));

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        updateDateRangeFilters: (columnId, selectedFilters) => {
          setState((state) => ({
            between: {
              ...state.between,
              [columnId]: selectedFilters
            },
            pagination: { ...state.pagination, page: 1 }
          }));

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        updateSort: (columnId, order) => {
          setState(() => ({
            sort: [columnId, order],
            pagination: { ...getState().pagination, page: 1 }
          }));
          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        updatePagination: (newPage: number, newRowsPerPage: number) => {
          const { rowsPerPage: currentRowsPerPage } = getState().pagination;

          const nextPage = newRowsPerPage !== currentRowsPerPage ? 1 : newPage;

          setState((state) => ({
            pagination: {
              ...state.pagination,
              page: nextPage,
              rowsPerPage: newRowsPerPage
            }
          }));

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        clearSearch: (columnId: string) => {
          setState((state) => {
            const newSearch = { ...state.search };
            delete newSearch[columnId];

            return {
              search: newSearch,
              pagination: { ...state.pagination, page: 1 }
            };
          });

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        clearFilters: (columnId: string) => {
          setState((state) => {
            const newFilters = { ...state.filters };
            delete newFilters[columnId];

            return {
              filters: newFilters,
              pagination: { ...state.pagination, page: 1 }
            };
          });

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        clearDateRangeFilters: (columnId: string) => {
          setState((state) => {
            const newBetween = { ...state.between };
            delete newBetween[columnId];

            return {
              between: newBetween,
              pagination: { ...state.pagination, page: 1 }
            };
          });

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        clearSort: () => {
          setState((state) => ({
            sort: ['', SortOrder.Ascending],
            pagination: { ...state.pagination, page: 1 }
          }));

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        },

        clearAllFilters: () => {
          setState((state) => ({
            search: {},
            filters: {},
            between: {},
            sort: ['', SortOrder.Ascending],
            pagination: { ...state.pagination, page: 1 }
          }));

          const apiFunc = getState().apiFunction;
          getState().setApiFunctionAndFetchData(apiFunc);
        }
      }),
      { name: 'TableStore' }
    )
  );

export default createTableStore;
